import React, { Suspense, lazy } from "react"
import { useSelector } from "react-redux"
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom"

import Loader from "shared/components/Loader/Global"
import LayoutCommon from "shared/layouts/Common/Common"
import LayoutWithNav from "shared/layouts/WithNav"
import Plans from "views/User/views/Plans"

import FallbackComponent, { fallbackRoutes } from "../views/FallbackComponent"
import PrefetchLongRequests from "../views/Prefetch"
import PlatformAccountRoot from "../views/Root"
import Services from "../views/Services/Services"

const NewEbayEsbAccount = lazy(() =>
  import("../views/NewAccount/eBayESB/NewAccount")
)

const CheckoutV3 = lazy(() => import("../views/CheckoutV3"))
const User = lazy(() => import("../views/User/User"))
const Token = lazy(() => import("../views/Token/Token"))
const TermsAndConditions = lazy(() =>
  import("../views/TermsAndConditions/TermsAndConditions")
)

export default function AuthenticatedRoutes() {
  const location = useLocation()
  const searchParams = useSearchParams()

  const shouldStartEbayOnboarding = useSelector(
    state => state.ebayOnboarding.shouldBeOnboarded
  )
  const { activeAccount, activePlatform } = useSelector(state => state.general)
  const userId = useSelector(state => state.user.id)
  const wiShippingEnabled = useSelector(
    state => state.settings.data["wi-shipping-enabled"]
  )
  const wiReturnsEnabled = useSelector(
    state => state.settings.data["wi-returns-enabled"]
  )
  const isReturnsEligible = useSelector(
    state =>
      state.entitlements.can_onboard_to_returns_domestic ||
      state.entitlements.can_onboard_to_returns_international_active
  )
  const isShippingEligible = useSelector(
    state => state.entitlements.can_use_shipping
  )

  return (
    <>
      <Routes location={location}>
        <Route element={<LayoutCommon />}>
          <Route
            path="*"
            element={
              <>
                <Navigate
                  to={
                    shouldStartEbayOnboarding
                      ? "/onboarding"
                      : `/${activePlatform}/${activeAccount}/dashboard`
                  }
                />
                <Outlet />
              </>
            }
          ></Route>

          <Route
            path="/onboarding/*"
            element={
              <Suspense fallback={<Loader floating={false} />}>
                <NewEbayEsbAccount />
              </Suspense>
            }
          />
          <Route
            path="/terms-and-conditions/*"
            element={
              <Suspense fallback={<Loader floating={false} />}>
                <TermsAndConditions />
              </Suspense>
            }
          />

          <Route
            path="/v3/checkout/*"
            element={
              <Suspense fallback={<Loader floating={false} />}>
                <CheckoutV3 />
              </Suspense>
            }
          />

          <Route
            path="/token/*"
            element={
              <Suspense fallback={<Loader floating={false} />}>
                <Token />
              </Suspense>
            }
          />
          <Route path="/plans/*" element={<Plans />} />
          <Route
            path="/profile/accounts/new-token"
            element={<Navigate to={`/token${searchParams.toString()}`} />}
          />
        </Route>
        <Route element={<LayoutWithNav />}>
          <Route
            path="/:platform/:account/*"
            element={<PlatformAccountRoot />}
          />
          <Route
            path="/shipping"
            element={
              <Services
                userId={userId}
                wiShippingEnabled={wiShippingEnabled}
                wiReturnsEnabled={wiReturnsEnabled}
                isReturnsEligible={isReturnsEligible}
                isShippingEligible={isShippingEligible}
              />
            }
          />
          <Route
            path="/returns"
            element={
              <Services
                userId={userId}
                wiShippingEnabled={wiShippingEnabled}
                wiReturnsEnabled={wiReturnsEnabled}
                isReturnsEligible={isReturnsEligible}
                isShippingEligible={isShippingEligible}
              />
            }
          />
          <Route
            path="/user/*"
            element={
              <Suspense fallback={<Loader floating={false} />}>
                <User />
              </Suspense>
            }
          />
          {fallbackRoutes.map(route => (
            <Route key={route} path={route} element={<FallbackComponent />} />
          ))}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <PrefetchLongRequests />
    </>
  )
}
